import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthForm } from '@/components/auth/AuthForm';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';

// Import new components
import ProductShowcase from '@/components/auth/ProductShowcase';
import SocialProof from '@/components/auth/SocialProof';
import AuthFormCard from '@/components/auth/AuthFormCard';
import { AuthView } from '@/components/auth/types';

// Import animations
import '@/styles/auth-animations.css';

export const Auth = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { toast } = useToast();
	const view = searchParams.get('view') as
		| 'sign_in'
		| 'sign_up'
		| 'forgot_password'
		| 'reset_password'
		| 'verification_code'
		| 'confirm_reset_password'
		| null;
	const type = searchParams.get('type');
	const token = searchParams.get('token');
	const email = searchParams.get('email');
	const confirmationUrl = searchParams.get('confirmation_url');

	// Set page title based on view
	useEffect(() => {
		const titles = {
			sign_in: 'Sign In | Strykr.ai',
			sign_up: 'Sign Up | Strykr.ai', 
			forgot_password: 'Reset Password | Strykr.ai',
			reset_password: 'Reset Password | Strykr.ai',
			verification_code: 'Verify Email | Strykr.ai',
			confirm_reset_password: 'Confirm Reset | Strykr.ai'
		};
		document.title = titles[view] || 'Authentication | Strykr.ai';
	}, [view]);

	useEffect(() => {
		let mounted = true; // Prevent state updates on unmounted component

		// Handle confirmation URL for password reset
		if (view === 'confirm_reset_password' && !confirmationUrl) {
			console.log('[Auth] Missing confirmation URL for password reset confirmation');
			toast({
				variant: 'destructive',
				title: 'Invalid password reset link',
				description: 'Please request a new password reset link from the forgot password page.',
			});
			navigate('/auth?view=forgot_password', { replace: true });
		}

		// Handle email confirmation
		const handleEmailConfirmation = async () => {
			if (type === 'email_confirmation' && token) {
				try {
					const email = searchParams.get('email');

					if (!email) {
						console.error('[Auth] Email address not found in verification URL');
						throw new Error('Email address not found for verification');
					}

					console.log('[Auth] Verifying email:', email);

					if (mounted) {
						toast({
							title: 'Verifying your email...',
							description: 'Please wait while we verify your email address.',
						});
					}

					const { error } = await supabase.auth.verifyOtp({
						token,
						type: 'email',
						email,
					});

					if (error) throw error;

					if (mounted) {
						toast({
							title: 'Email verified successfully!',
							description: 'You can now sign in with your email and password.',
						});

						setTimeout(() => {
							navigate('/auth?view=sign_in');
						}, 1500);
					}
				} catch (error) {
					console.error('[Auth] Error verifying email:', error);
					
					if (mounted) {
						let errorMessage = 'Failed to verify email. Please try again.';
						if (error instanceof Error) {
							errorMessage = error.message;
						}
						
						toast({
							variant: 'destructive',
							title: 'Error verifying email',
							description: errorMessage,
						});
						navigate('/auth?view=sign_in');
					}
				}
			}
		};

		handleEmailConfirmation();

		return () => {
			mounted = false;
		};
	}, [searchParams, navigate, toast, type, token, view, confirmationUrl]);

	useEffect(() => {
		// Check if we already have a session when the component mounts
		const checkExistingSession = async () => {
			try {
				const { data: { session }, error } = await supabase.auth.getSession();
				
				if (error) {
					console.error('[Auth] Error checking session:', error);
					return;
				}
				
				if (session?.user) {
					console.log('[Auth] Existing session found, checking onboarding status');
					handleSuccessfulAuth(session);
				}
			} catch (err) {
				console.error('[Auth] Error in existing session check:', err);
			}
		};
		
		// Only check for existing session if we're on the sign-in page
		if (view === 'sign_in' || !view) {
			checkExistingSession();
		}
		
		// Handler function for successful authentication
		const handleSuccessfulAuth = async (session: Session) => {
			if (!session?.user?.id) return;
			
			try {
				// Check if user has completed onboarding
				const { data: onboardingData, error: onboardingError } = await supabase.rpc(
					'check_onboarding_status',
					{
						user_id: session.user.id,
					}
				);

				if (onboardingError) {
					console.error('[Auth] Error checking onboarding status:', onboardingError);
					return;
				}

				if (!onboardingData) {
					navigate('/onboarding');
				} else {
					navigate('/');
				}
			} catch (err) {
				console.error('[Auth] Error checking onboarding after auth:', err);
			}
		};
		
		// Set up auth state change listener
		const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
			console.log('[Auth] Auth state change:', event);

			if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
				if (session) {
					handleSuccessfulAuth(session);
				}
			}

			if (event === 'SIGNED_OUT') {
				navigate('/auth?view=sign_in');
			}
		});

		return () => {
			authListener.subscription.unsubscribe();
		};
	}, [navigate, view]);

	return (
		<div className="relative flex w-full h-[100dvh] items-center justify-center overflow-hidden px-4 sm:px-6 bg-gradient-to-br from-gray-900 via-blue-900/20 to-gray-900">
			{/* Background decorations */}
			<div 
				className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10 pointer-events-none" 
				aria-hidden="true"
			/>
			<div 
				className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-blue-500/10 blur-3xl pointer-events-none motion-reduce:opacity-0" 
				aria-hidden="true"
			/>
			<div 
				className="absolute top-0 left-0 h-[28rem] w-[28rem] rounded-full bg-purple-500/10 blur-3xl pointer-events-none motion-reduce:opacity-0" 
				aria-hidden="true"
			/>

			{/* Centered two-panel card */}
			<div className="relative z-10 flex w-full max-w-sm sm:max-w-md lg:max-w-5xl xl:max-w-6xl flex-col lg:flex-row overflow-hidden rounded-xl lg:rounded-2xl shadow-xl lg:shadow-2xl">
				{/* Showcase – hidden on small */}
				<aside 
					className="hidden basis-3/5 flex-col justify-between bg-black/20 p-8 backdrop-blur-sm lg:flex border-r border-gray-800/40"
					aria-label="Product features and benefits"
				>
					<ProductShowcase />
					<SocialProof />
				</aside>

				{/* Auth panel */}
				<main 
					className="flex flex-1 items-center justify-center bg-black/10 p-6"
					role="main"
					aria-label={`${view === 'sign_up' ? 'Sign up' : view === 'forgot_password' ? 'Reset password' : 'Sign in'} form`}
				>
					<div className="w-full max-w-md">
						<AuthFormCard view={view || 'sign_in'} />
					</div>
				</main>
			</div>
		</div>
	);
};
